<script setup>
/**
 * The hub's identity at the top of the sidebar, at both widths (XN-4507).
 *
 * Open: the full logo above the name when one is set; otherwise the name on
 * its own. The name IS the wordmark when there is no logo — a coloured chip
 * with a letter in it was never a logo, it was a chip pretending to be one,
 * and it read as branding the agency never chose.
 *
 * Collapsed: the square mark if there is one; else the full logo scaled down
 * (unreadable at 4:1, which is why the mark exists, but a scaled logo is
 * still the hub's own); else the first letter. Cropping a wordmark to a
 * square would leave half a word, so it is scaled, never cropped.
 *
 * Mirrors the prototype's brandBlock() and the ladder in AC7. The preview on
 * Hub settings draws the same rules, so what an owner sees there is what
 * this does.
 */
import { computed } from 'vue';
import { roleLabel } from '../../helpers/roles';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';

defineProps({
  collapsed: { type: Boolean, default: false },
});

const eventStore = useEventStore();
const authStore = useAuthStore();

// NOTE: `eventStore.event` is a reactive({}) holding a key literally named
// `value` — not a ref. Reading `.value.x` inside a computed tracks correctly;
// storeToRefs(eventStore).event does not do what it looks like it does.
//
// The payload is awaited in index.js before the app mounts, so the logo and
// name are there on first paint — no skeleton, no flash of the wrong brand.
const logo = computed(() => eventStore.event.value?.login_logo || null);
const mark = computed(() => eventStore.event.value?.login_logo_mark || null);
const hubName = computed(
  () => eventStore.event.value?.login_event_name || window.store?.subdomain || 'Hub'
);

const initial = computed(() => hubName.value.trim().charAt(0).toUpperCase() || 'H');

// Which hat the viewer is wearing, from the same map the top bar uses.
const context = computed(() => `Agency Hub · ${roleLabel(authStore.userRole)}`);
</script>

<template>
  <div class="hub-brand" :class="{ 'hub-brand--collapsed': collapsed }" :title="collapsed ? hubName : undefined">
    <template v-if="collapsed">
      <img v-if="mark" :src="mark" :alt="hubName" class="hub-brand__mark" />
      <img v-else-if="logo" :src="logo" :alt="hubName" class="hub-brand__scaled" />
      <span v-else class="hub-brand__monogram" aria-hidden="true">{{ initial }}</span>
    </template>

    <template v-else>
      <div v-if="logo" class="hub-brand__slot">
        <img :src="logo" :alt="hubName" class="hub-brand__logo" />
      </div>
      <div class="hub-brand__text">
        <span class="hub-brand__name" :title="hubName">{{ hubName }}</span>
        <span class="hub-brand__context">{{ context }}</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.hub-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  padding: 12px 8px;
  min-width: 0;
}

.hub-brand--collapsed {
  align-items: center;
  padding: 12px 0;
}

/* The same 44px-tall box the logo was uploaded into, so it lands here looking
   exactly as it did in Settings. */
.hub-brand__slot {
  height: var(--xl-brand-logo-height, 44px);
  max-width: 100%;
}

.hub-brand__logo {
  display: block;
  height: 100%;
  width: auto;
  max-width: 100%;
  object-fit: contain;
}

.hub-brand__mark {
  width: var(--xl-brand-mark-size, 30px);
  height: var(--xl-brand-mark-size, 30px);
  object-fit: contain;
  flex-shrink: 0;
}

.hub-brand__scaled {
  width: var(--xl-brand-scaled-width, 52px);
  height: var(--xl-brand-scaled-height, 18px);
  object-fit: contain;
  flex-shrink: 0;
}

.hub-brand__monogram {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--xl-brand-monogram-size, 36px);
  height: var(--xl-brand-monogram-size, 36px);
  flex-shrink: 0;
  border-radius: 10px;
  background: var(--xl-color-primary, #5e2f78);
  color: var(--xl-color-on-primary, #fff);
  font-family: var(--xl-font-family);
  font-size: 16px;
  font-weight: 700;
}

.hub-brand__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

/* Stacked under the logo, the name gets the sidebar's full width — enough
   for the 50-character limit over two lines before it has to truncate. */
.hub-brand__name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-family: var(--xl-font-family);
  font-size: 14.5px;
  font-weight: 700;
  line-height: normal;
  color: var(--xl-color-on-surface);
}

.hub-brand__context {
  font-family: var(--xl-font-family);
  font-size: 12px;
  line-height: normal;
  color: var(--XL-M-Theme-sys-light-on-surface-variant, #4b444d);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
